import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Paper,
  TextField,
  Button,
  Box,
  IconButton,
  Alert,
  CircularProgress
} from '@mui/material';
import { CloudUpload, Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { uploadActivity } from '../services/api/activity';
import { showAlert } from '../components/service/AlertService';

const UploadActivity = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    file: null,
    airDensity: '',
    cyclistWeight: '',
    luggageWeight: '',
    rollingResistanceCoefficient: '',
    mechanicalEfficiency: ''
  });
  const [fileName, setFileName] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.name.endsWith('.tcx')) {
      setFormData(prev => ({
        ...prev,
        file: file
      }));
      setFileName(file.name);
    } else {
      showAlert("INVALID_FILE_FORMAT");
    }
  };

  const handleRemoveFile = () => {
    setFormData(prev => ({
      ...prev,
      file: null
    }));
    setFileName('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      // Leer el contenido del archivo TCX como texto
      const fileContent = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsText(formData.file);
      });

      const activityData = {
        name: formData.name,
        description: formData.description,
        airDensity: parseFloat(formData.airDensity),
        cyclistWeight: parseFloat(formData.cyclistWeight),
        luggageWeight: parseFloat(formData.luggageWeight),
        rollingResistanceCoefficient: parseFloat(formData.rollingResistanceCoefficient),
        mechanicalEfficiency: parseFloat(formData.mechanicalEfficiency),
        data: fileContent
      };

      let data = await uploadActivity(activityData);
      navigate(`/activity/${data.data.activityId}`);
    } catch (error) {
      showAlert("ERROR_UPLOAD_ACTIVITY");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 12, mb: 4 }}>
      <Typography 
        variant="h4" 
        component="h1" 
        sx={{ 
          mb: 4,
          fontWeight: 300,
          letterSpacing: '0.1em'
        }}
      >
        {t('uploadActivity.title')}
      </Typography>
      <Paper 
        component="form"
        onSubmit={handleSubmit}
        elevation={0}
        sx={{ 
          p: 4,
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          borderRadius: 2,
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <TextField
          fullWidth
          required
          label={t('uploadActivity.activityName')}
          value={formData.name}
          onChange={handleChange('name')}
          sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'rgba(255, 255, 255, 0.03)',
            }
          }}
        />

        <TextField
          fullWidth
          required
          label={t('uploadActivity.description')}
          value={formData.description}
          onChange={handleChange('description')}
          sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'rgba(255, 255, 255, 0.03)',
            }
          }}
        />

        <TextField
          fullWidth
          required
          label={t('uploadActivity.airDensity')}
          type="number"
          value={formData.airDensity}
          onChange={handleChange('airDensity')}
          sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'rgba(255, 255, 255, 0.03)',
            }
          }}
        />

        <TextField
          fullWidth
          required
          label={t('uploadActivity.cyclistWeight')}
          type="number"
          value={formData.cyclistWeight}
          onChange={handleChange('cyclistWeight')}
          sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'rgba(255, 255, 255, 0.03)',
            }
          }}
        />

        <TextField
          fullWidth
          required
          label={t('uploadActivity.luggageWeight')}
          type="number"
          value={formData.luggageWeight}
          onChange={handleChange('luggageWeight')}
          sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'rgba(255, 255, 255, 0.03)',
            }
          }}
        />

        <TextField
          fullWidth
          required
          label={t('uploadActivity.rollingResistanceCoefficient')}
          type="number"
          value={formData.rollingResistanceCoefficient}
          onChange={handleChange('rollingResistanceCoefficient')}
          sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'rgba(255, 255, 255, 0.03)',
            }
          }}
        />

        <TextField
          fullWidth
          required
          label={t('uploadActivity.mechanicalEfficiency')}
          type="number"
          value={formData.mechanicalEfficiency}
          onChange={handleChange('mechanicalEfficiency')}
          sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'rgba(255, 255, 255, 0.03)',
            }
          }}
        />

        <Box 
          sx={{ 
            mb: 3,
            p: 3,
            border: '2px dashed rgba(33, 150, 243, 0.5)',
            borderRadius: 2,
            textAlign: 'center',
            cursor: 'pointer',
            '&:hover': {
              borderColor: 'primary.main'
            }
          }}
          onClick={() => document.getElementById('file').click()}
        >
          <input
            type="file"
            id="file"
            accept=".tcx"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
          <Typography variant="body1" sx={{ mb: 1 }}>
            {t('uploadActivity.dragDropZone')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('uploadActivity.orClickToSelect')}
          </Typography>
        </Box>

        {fileName && (
          <Box 
            sx={{ 
              mb: 3,
              p: 2,
              backgroundColor: 'rgba(255, 255, 255, 0.03)',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Typography variant="body2">
              {fileName}
            </Typography>
            <IconButton 
              size="small" 
              onClick={handleRemoveFile}
              sx={{ color: 'error.main' }}
            >
              <Close />
            </IconButton>
          </Box>
        )}

        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={!formData.name || !formData.file || !formData.airDensity || !formData.cyclistWeight || !formData.luggageWeight || !formData.rollingResistanceCoefficient || !formData.mechanicalEfficiency}
          sx={{
            textTransform: 'none',
            letterSpacing: '0.1em',
            fontWeight: 300,
            color: '#ffffff',
            background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
            '&:hover': {
              background: 'linear-gradient(45deg, #2196F3 60%, #21CBF3 90%)',
            },
          }}
          startIcon={loading ? <CircularProgress size={24} /> : null}
        >
          {t('uploadActivity.upload')}
        </Button>
      </Paper>
    </Container>
  );
};

export default UploadActivity;
